import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-locale templates for the WhatsApp seller-contact message. Keys
 * match BCP-47 codes from `platform_languages`. {title}, {location},
 * {price} are replaced at send time.
 *
 * Adding a new locale to the platform without an entry here? The
 * default fallback (FR) is used so the message is never blank.
 */
const TEMPLATES = {
  fr: `Bonjour ! J'ai vu votre annonce sur ATÔ :

« {title} »
📍 {location}
💰 {price}

Est-elle toujours disponible ? Merci !`,
  en: `Hello! I saw your listing on ATÔ:

"{title}"
📍 {location}
💰 {price}

Is it still available? Thank you!`,
  mg: `Salama! Nahita ny dokambarotrao tao amin'ny ATÔ aho:

« {title} »
📍 {location}
💰 {price}

Mbola misy ve? Misaotra!`,
};
const FALLBACK_LOCALE = 'fr';

/**
 * Resolve the language a contact-message TARGETED AT `sellerId` should
 * be written in. The backend implements the priority chain:
 *   seller.preferred_language → seller.ui_language → platform default.
 * Falls back to FR if the lookup fails (e.g. offline).
 */
export const fetchSellerMessageLanguage = async (sellerId) => {
  if (!sellerId) return FALLBACK_LOCALE;
  try {
    const { data } = await axios.get(`${API}/users/${sellerId}/message-language`);
    return data?.language || FALLBACK_LOCALE;
  } catch {
    return FALLBACK_LOCALE;
  }
};

/**
 * Build a WhatsApp `wa.me/<phone>?text=...` URL with the message
 * pre-filled in the seller's chosen language.
 *
 * @param {object} args
 * @param {string} args.phone     — raw seller phone (we strip non-digits).
 * @param {string} args.title     — listing title.
 * @param {string} args.location  — formatted location (e.g. "Antananarivo").
 * @param {string} args.price     — formatted price string.
 * @param {string} args.language  — BCP-47 locale; falls back to FR.
 */
export const buildWhatsappContactUrl = ({ phone, title, location, price, language }) => {
  const tpl = TEMPLATES[language] || TEMPLATES[FALLBACK_LOCALE];
  const message = tpl
    .replace('{title}', title || '')
    .replace('{location}', location || '')
    .replace('{price}', price || '');
  const cleanPhone = String(phone || '').replace(/[^\d]/g, '');
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
};
